import { DollarSign, FileText, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';

interface AiScenarioPreviewProps {
  procedures: LocalScenarioProcedure[];
  scenarioName?: string;
  isBuilding?: boolean;
}

export function AiScenarioPreview({ procedures, scenarioName, isBuilding }: AiScenarioPreviewProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const totalCost = procedures.reduce((sum, p) => sum + p.estimatedCost * p.quantity, 0);
  const phases = Array.from(new Set(procedures.map(p => p.phase).filter(Boolean)));

  return (
    <div className={`h-full flex flex-col rounded-xl border ${
      ahpDark ? 'border-civic-gold/20 bg-civic-card-dark' : isAhp ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-black/30'
    }`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${isAhp ? 'border-gray-200' : 'border-white/10'}`}>
        <div className="flex items-center gap-2">
          <FileText className={`w-4 h-4 ${isAhp ? 'text-civic-money' : 'text-amber-400'}`} />
          <h3 className={`text-sm font-bold ${ahpDark ? 'text-civic-warm' : isAhp ? 'text-gray-900' : 'text-foreground'}`}>
            {scenarioName || 'Your Scenario'}
          </h3>
          {isBuilding && (
            <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Cost summary */}
      <div className={`px-4 py-3 border-b ${isAhp ? 'border-gray-200' : 'border-white/10'}`}>
        <div className="flex items-center gap-2">
          <DollarSign className={`w-5 h-5 ${isAhp ? 'text-civic-money' : 'text-amber-400'}`} />
          <span className={`text-2xl font-mono font-bold ${isAhp ? 'text-civic-money' : 'text-amber-400'}`}>
            ${totalCost.toLocaleString()}
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {procedures.length} procedure{procedures.length !== 1 ? 's' : ''} across {phases.length} phase{phases.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Procedure list */}
      <div className="flex-1 overflow-y-auto thin-scrollbar p-3 space-y-1.5">
        {procedures.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <FileText className="w-8 h-8 text-muted-foreground/30 mb-2" />
            <p className="text-xs text-muted-foreground">
              {isBuilding ? 'Building your scenario...' : 'Procedures will appear here as the AI adds them'}
            </p>
          </div>
        ) : (
          procedures.map((proc, idx) => (
            <div
              key={`${proc.code}-${idx}`}
              className={`p-2 rounded-lg border transition-all ${
                idx === procedures.length - 1 && isBuilding
                  ? isAhp
                    ? ahpDark ? 'border-amber-500/30 bg-amber-500/10 animate-pulse' : 'border-amber-300 bg-amber-50 animate-pulse'
                    : 'border-amber-500/30 bg-amber-500/5 animate-pulse'
                  : ahpDark
                    ? 'border-civic-gold/15 bg-[var(--civic-navy-deep)]'
                    : isAhp
                    ? 'border-gray-200 bg-white'
                    : 'border-white/5 bg-black/20'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <Badge variant="outline" className="text-[8px] px-1 py-0">
                  {proc.codeType}
                </Badge>
                <span className={`font-mono text-[10px] ${isAhp ? 'text-civic-money' : 'text-amber-400'}`}>
                  {proc.code}
                </span>
                {proc.phase && (
                  <span className="text-[8px] text-muted-foreground/60 ml-auto">
                    {proc.phase}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground leading-tight">{proc.description}</p>
              <div className="flex items-center gap-2 mt-1 text-[10px]">
                {proc.quantity > 1 && <span className="text-muted-foreground">x{proc.quantity}</span>}
                <span className={`font-mono ${isAhp ? 'text-gray-700' : 'text-foreground/70'}`}>
                  ${(proc.estimatedCost * proc.quantity).toLocaleString()}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
